/**
 * Prepare files for album (and similar) uploads: validate size, pass through server-native
 * types without re-encode, convert HEIC via heic2any+Jimp, optional JPEG normalize with fallback to original.
 */

import { convertToJpeg, downscaleRasterIfOverMaxEdge } from './browserPhotoToJpeg';
import {
   ALBUM_UPLOAD_PASS_THROUGH_IMAGE_MIMES,
   isAlbumUploadAllowedMime,
   resolveAlbumUploadMime,
} from './albumUploadConstants';

const HEIC_EXTENSIONS = ['heic', 'heif'];
const RAW_EXTENSIONS = ['cr2', 'cr3', 'nef', 'arw', 'dng', 'raf', 'orf', 'rw2', 'raw'];

export interface PrepareOptions {
   maxSizeBytes?: number;
   maxCount?: number;
   onProgress?: (phase: 'converting', done: number, total: number) => void;
}

export interface PrepareResult {
   prepared: File[];
   preparedByIndex: (File | null)[];
   errors: { file: File; error: string }[];
}

function fileExtension(file: File): string {
   return file.name.split('.').pop()?.toLowerCase() ?? '';
}

function isHeicFile(file: File): boolean {
   const ext = fileExtension(file);
   if (HEIC_EXTENSIONS.includes(ext)) return true;
   const t = (file.type || '').toLowerCase();
   return t === 'image/heic' || t === 'image/heif';
}

function isRawFile(file: File): boolean {
   return RAW_EXTENSIONS.includes(fileExtension(file));
}

function normalizeImageMime(mime: string): string {
   const m = mime.toLowerCase();
   return m === 'image/jpg' ? 'image/jpeg' : m;
}

function passThroughFile(file: File, mimeType: string): File {
   const mt = normalizeImageMime(mimeType);
   return new File([file], file.name, {
      type: mt,
      lastModified: file.lastModified,
   });
}

async function jpegResultToFile(file: File, result: Awaited<ReturnType<typeof convertToJpeg>>): Promise<File> {
   const res = await fetch(result.base64);
   const blob = await res.blob();
   const name = file.name.replace(/\.[^.]+$/i, '.jpg') || 'photo.jpg';
   return new File([blob], name, { type: 'image/jpeg' });
}

function shouldFallbackToOriginal(file: File, resolvedMime: string | null, err: unknown): boolean {
   if (isHeicFile(file) || isRawFile(file)) return false;
   if (!resolvedMime || !isAlbumUploadAllowedMime(resolvedMime)) return false;
   return true;
}

/**
 * Prepares files for upload: validates size, passes through album-allowed image/video without re-encode,
 * converts HEIC only; on Jimp/conversion failure for other allowed types, returns original file.
 */
export async function preparePhotosForUpload(
   files: File[],
   options: PrepareOptions = {}
): Promise<PrepareResult> {
   const { maxSizeBytes, maxCount, onProgress } = options;
   const list = maxCount != null ? files.slice(0, maxCount) : files;
   const total = list.length;
   const errors: { file: File; error: string }[] = [];

   const results = await Promise.allSettled(
      list.map(async (file, index) => {
         if (maxSizeBytes != null && file.size > maxSizeBytes) {
            throw new Error(
               `File "${file.name}" is too large (${(file.size / (1024 * 1024)).toFixed(1)}MB). Maximum is ${(maxSizeBytes / (1024 * 1024)).toFixed(0)}MB.`
            );
         }

         if (isRawFile(file)) {
            throw new Error(
               'RAW files are not supported in the browser. Please use JPEG, PNG, HEIC, or video.'
            );
         }

         onProgress?.('converting', index, total);

         if (isHeicFile(file)) {
            try {
               const result = await convertToJpeg(file, () => {
                  onProgress?.('converting', index, total);
               });
               return await jpegResultToFile(file, result);
            } catch (e) {
               const msg = e instanceof Error ? e.message : String(e);
               throw new Error(`HEIC conversion failed: ${msg}`);
            }
         }

         const resolved = resolveAlbumUploadMime(file);
         if (!resolved) {
            throw new Error(
               `Unsupported format: "${file.name}". Use a supported image or video type.`
            );
         }

         if (resolved.startsWith('video/')) {
            return passThroughFile(file, resolved);
         }

         const normalized = normalizeImageMime(resolved);
         if (
            (ALBUM_UPLOAD_PASS_THROUGH_IMAGE_MIMES as readonly string[]).includes(normalized) ||
            (ALBUM_UPLOAD_PASS_THROUGH_IMAGE_MIMES as readonly string[]).includes(resolved)
         ) {
            if (normalized === 'image/svg+xml' || normalized === 'image/gif') {
               return passThroughFile(file, normalized);
            }
            try {
               return await downscaleRasterIfOverMaxEdge(file, normalized);
            } catch (e) {
               if (shouldFallbackToOriginal(file, resolved, e)) {
                  return passThroughFile(file, normalized);
               }
               throw e instanceof Error ? e : new Error(String(e));
            }
         }

         try {
            const result = await convertToJpeg(file, () => {
               onProgress?.('converting', index, total);
            });
            return await jpegResultToFile(file, result);
         } catch (e) {
            if (shouldFallbackToOriginal(file, resolved, e)) {
               return passThroughFile(file, normalized);
            }
            throw e instanceof Error ? e : new Error(String(e));
         }
      })
   );

   const preparedByIndex: (File | null)[] = results.map((outcome, i) => {
      if (outcome.status === 'fulfilled') return outcome.value;
      errors.push({
         file: list[i],
         error: outcome.reason?.message ?? String(outcome.reason),
      });
      return null;
   });

   const prepared = preparedByIndex.filter((f): f is File => f != null);

   return { prepared, preparedByIndex, errors };
}
